import { useEffect, useMemo, useRef } from 'react';
import { useResponseErrors } from '~/contexts/ErrorContext';
import { useUserDeliveryInfo } from '~/contexts/InventoryContext';
import { Vehicle } from '~/contracts';
import useTermsAreLocked from '~/hooks/useTermsAreLocked';
import { getVehicleData } from '../helpers';

type OwnProps = {
  latestInventoryVehicles: Vehicle[];
  latestSimilarVehicles: Partial<Vehicle>[];
  shouldHaveSimilarVehicles: boolean;
};

export default function useGetVehiclesLoaded({
  latestInventoryVehicles,
  latestSimilarVehicles,
  shouldHaveSimilarVehicles
}: OwnProps) {
  const { availabilityError, lowAvailabilityError, pricingError } =
    useResponseErrors();

  const userDeliveryInfo = useUserDeliveryInfo();
  const userDeliveryInfoRef = useRef(userDeliveryInfo);
  const termsAreLocked = useTermsAreLocked();

  useEffect(() => {
    userDeliveryInfoRef.current = userDeliveryInfo;
  }, [userDeliveryInfo]);

  return useMemo(() => {
    const inventoryVehicles = getVehicleData(latestInventoryVehicles);
    const hasPricing =
      termsAreLocked ||
      Boolean(pricingError) ||
      inventoryVehicles.some(
        v => v.tileInfo.hasTerms || v.tileInfo.pricingErrorMessage
      );

    const hasAvailability =
      userDeliveryInfoRef.current?.isOutOfMarket === true ||
      Boolean(availabilityError) ||
      inventoryVehicles.some(v => Boolean(v.pickupInfo || v.deliveryInfo));

    const inventoryVehiclesLoaded =
      inventoryVehicles.length === 0 || (hasPricing && hasAvailability);

    const similarVehiclesToSend = shouldHaveSimilarVehicles
      ? getVehicleData(latestSimilarVehicles)
      : [];

    const similarVehiclesAvailability =
      Boolean(lowAvailabilityError) ||
      similarVehiclesToSend.some(v => Boolean(v.pickupInfo || v.deliveryInfo));

    const similarVehiclesLoaded =
      similarVehiclesToSend.length > 0 &&
      similarVehiclesAvailability &&
      similarVehiclesToSend.some(
        v => v.tileInfo.hasTerms || v.tileInfo.pricingErrorMessage
      );

    return {
      loadedInventoryVehicles: {
        isLoaded: inventoryVehiclesLoaded,
        vehicles: inventoryVehicles
      },
      loadedSimilarVehicles: {
        isLoaded: similarVehiclesLoaded,
        vehicles: similarVehiclesToSend
      }
    };
  }, [
    availabilityError,
    latestInventoryVehicles,
    latestSimilarVehicles,
    lowAvailabilityError,
    pricingError,
    shouldHaveSimilarVehicles,
    termsAreLocked
  ]);
}
